/**
 * Template editor — Unlayer drag & drop, with an HTML fallback.
 *
 * Unlayer is loaded from its CDN as a plain <script>. Two things matter:
 *
 *  1. embed.js is UMD, so with RequireJS present it registers as an anonymous
 *     AMD module and never reaches `window.unlayer`. We hide `define.amd`
 *     while it loads and restore it after — the same trick this dashboard
 *     already uses for html2canvas.
 *  2. If the CDN is unreachable (offline, blocked), the editor falls back to a
 *     raw HTML textarea rather than showing nothing. A template stays editable
 *     and sendable either way, because `htmlContent` is what we actually send —
 *     `designJson` only exists so Unlayer can reopen its own document.
 */
define('mailingTemplateEditorView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  var UNLAYER_SCRIPT = 'https://editor.unlayer.com/embed.js';
  var MERGE_TAGS = [
    'first_name', 'last_name', 'email', 'country_code', 'language',
    'webshop', 'webshop_locale', 'webshop_locale_phone',
    'offer_email_seq_picture1', 'offer_email_seq_url1', 'offer_email_seq_poi1',
    'offer_email_seq_price1', 'offer_email_seq_quantity1',
    'end_user_sec_id', 'unsubscribe_url'
  ];

  /** Loads embed.js once per page; resolves with window.unlayer or rejects. */
  function loadUnlayer() {
    var defer = $.Deferred();
    if (window.unlayer) {
      return defer.resolve(window.unlayer).promise();
    }
    var amd = window.define ? window.define.amd : null;
    if (window.define) {
      window.define.amd = null;
    }
    var script = document.createElement('script');
    script.src = UNLAYER_SCRIPT;
    script.onload = function () {
      if (window.define) {
        window.define.amd = amd;
      }
      if (window.unlayer) {
        defer.resolve(window.unlayer);
      } else {
        defer.reject();
      }
    };
    script.onerror = function () {
      if (window.define) {
        window.define.amd = amd;
      }
      defer.reject();
    };
    document.head.appendChild(script);
    return defer.promise();
  }

  var TemplateEditorView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-template-editor',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-editor-head">' +
      '  <label class="inline">Name<input type="text" class="f-name"></label>' +
      '  <label class="inline grow">Subject<input type="text" class="f-subject"></label>' +
      '  <label class="inline narrow">Language<input type="text" class="f-language"></label>' +
      '</div>' +
      '<div class="mlg-merge-tags"></div>' +
      '<div class="mlg-editor-host"><div id="mailing-unlayer" class="mlg-unlayer"></div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      name: '.f-name',
      subject: '.f-subject',
      language: '.f-language',
      host: '.mlg-editor-host',
      tags: '.mlg-merge-tags',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-save-template': 'onSave',
      'click .mlg-back': 'onBack',
      'click .mlg-send-test': 'onSendTest',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-send': 'onSendTestConfirm'
    },

    initialize: function (options) {
      this.secId = options.secId;
      this.mode = 'unlayer';
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('templates',
          '<button type="button" class="mlg-btn mlg-send-test">Send test</button>' +
          '<button type="button" class="mlg-btn primary mlg-save-template">Save</button>' +
          '<a class="mlg-btn mlg-back" href="#mailing/templates">Back to list</a>')
      };
    },

    onShow: function () {
      var self = this;
      this.ui.tags.html('<span class="t-label">Merge fields — click to copy:</span>' +
        _.map(MERGE_TAGS, function (tag) {
          return '<code class="t-tag">{{' + tag + '}}</code>';
        }).join(''));

      MailingApi.template(this.secId).done(function (template) {
        self.model = template;
        self.ui.name.val(template.name || '');
        self.ui.subject.val(template.subject || '');
        self.ui.language.val(template.language || 'EN');
        self.mountEditor(template);
      }).fail(function (xhr) {
        self.ui.host.html('<div class="mlg-error">' + _.escape(MailingApi.errorMessage(xhr)) + '</div>');
      });
    },

    mountEditor: function (template) {
      var self = this;
      loadUnlayer().done(function (unlayer) {
        try {
          unlayer.init({
            id: 'mailing-unlayer',
            displayMode: 'email',
            mergeTags: _.reduce(MERGE_TAGS, function (tags, tag) {
              tags[tag] = { name: Shell.prettyCase(tag), value: '{{' + tag + '}}' };
              return tags;
            }, {})
          });
          var design = self.parseDesign(template.designJson);
          if (design) {
            unlayer.loadDesign(design);
          }
          self.unlayer = unlayer;
        } catch (error) {
          self.mountFallback(template, 'The drag & drop editor could not start.');
        }
      }).fail(function () {
        self.mountFallback(template,
          'The drag & drop editor could not be loaded (editor.unlayer.com unreachable).');
      });
    },

    /** Raw-HTML editing, used whenever Unlayer is unavailable. */
    mountFallback: function (template, reason) {
      this.mode = 'html';
      this.ui.host.html(
        '<div class="mlg-warning">' + _.escape(reason) +
          ' Editing the HTML source instead — it is what gets sent.</div>' +
        '<textarea class="mlg-html-source" spellcheck="false"></textarea>');
      this.ui.host.find('.mlg-html-source').val(template.htmlContent || '');
    },

    parseDesign: function (raw) {
      if (!raw) { return null; }
      try {
        var design = JSON.parse(raw);
        return design && design.body ? design : null;
      } catch (error) {
        return null;
      }
    },

    /**
     * Collects the current document. Unlayer's export is asynchronous, so this
     * hands back a promise in both modes and the callers stay identical.
     */
    currentContent: function () {
      var defer = $.Deferred();
      if (this.mode === 'html') {
        defer.resolve({
          htmlContent: this.ui.host.find('.mlg-html-source').val(),
          designJson: this.model ? this.model.designJson : ''
        });
        return defer.promise();
      }
      if (!this.unlayer) {
        defer.reject();
        return defer.promise();
      }
      this.unlayer.exportHtml(function (data) {
        defer.resolve({
          htmlContent: data.html,
          designJson: JSON.stringify(data.design)
        });
      });
      return defer.promise();
    },

    onSave: function () {
      var self = this;
      this.currentContent().done(function (content) {
        MailingApi.updateTemplate(self.secId, {
          name: self.ui.name.val(),
          subject: self.ui.subject.val(),
          language: self.ui.language.val(),
          htmlContent: content.htmlContent,
          designJson: content.designJson
        }).done(function () {
          Shell.banner(self.$el, 'Template saved', 'ok');
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
      }).fail(function () {
        Shell.banner(self.$el, 'The editor is not ready yet', 'error');
      });
    },

    onSendTest: function () {
      this.ui.modal.html(
        '<div class="mlg-modal-card narrow">' +
        '<h3>Send a test</h3>' +
        '<label>Recipient<input type="email" class="f-test-email" placeholder="you@example.com"></label>' +
        '<div class="mlg-hint">Merge fields are filled with sample data. The test bypasses the outbox ' +
          'and its hourly limit.</div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-send">Send</button>' +
        '</div>' +
        '</div>').show();
    },

    onSendTestConfirm: function () {
      var self = this;
      var email = this.ui.modal.find('.f-test-email').val();
      if (!email || email.indexOf('@') === -1) {
        Shell.banner(this.$el, 'A valid recipient is required', 'error');
        return;
      }
      this.currentContent().done(function (content) {
        MailingApi.sendTestEmail({
          email: email,
          subject: self.ui.subject.val(),
          htmlContent: content.htmlContent
        }).done(function () {
          self.closeModal();
          Shell.banner(self.$el, 'Test sent to ' + email, 'ok');
        }).fail(function (xhr) {
          Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
        });
      });
    },

    closeModal: function () {
      this.ui.modal.hide().empty();
    },

    onBack: function (e) {
      e.preventDefault();
      App.navigate('mailing/templates', { trigger: true });
    }
  });

  module.exports = TemplateEditorView;
});

